import type { ResourceProps } from 'react-admin';

import { PluginResource } from '@src/types';
import { PluginType } from '@troverecommerce/ps-configs-api-client';

/**
 * Application data models and enums
 * Contains application-specific enums and constants
 */

/**
 *
 * Defines the available resource types that can be displayed and managed as React Admin Resources
 * within the partner dashboard interface.
 *
 */
export const enum DashboardResources {
  /** Catalog items resource type for managing product catalog data */
  CatalogItems = 'catalog/products',
  /** Variant SKU resource type for managing individual product variants */
  VariantSkus = 'variant-skus',
  /** Resource type used to manage recycle plugin configurations. */
  RecyclePlugins = 'settings/plugins/recycle',
  /** Resource type used to manage repair plugin configurations. */
  RepairPlugins = 'settings/plugins/repair',
  /** Resource type used to manage resale plugin configurations. */
  ResalePlugins = 'settings/plugins/resale',
  /** Resource type used to manage trade-in plugin configurations. */
  TradeinPlugins = 'settings/plugins/tradein',
  /** Repairs resource type for managing repair orders */
  Repairs = 'repairs/submissions',
  /** Catalog fields mapping upload resource for uploading display type mappings */
  CatalogFieldsMappingUpload = 'catalog/fields',
  /** Pricing rules resource (resale pricing) */
  PricingRules = 'resale/pricing',
  /** Condition discounts resource (resale pricing) */
  ConditionDiscounts = 'resale/conditions',
  /** Item adjusted prices resource (resale pricing, read-only) */
  ItemAdjustedPrices = 'resale/overrides',
  /** Repair pricing criteria resource  */
  RepairPricing = 'repairs/pricing',
  /** Shopify product redirects resource (read-only visualization) */
  ShopifyRedirects = 'storefront/redirects',
  /** Catalog rules resource for if/then rule management */
  CatalogRules = 'catalog/rules',
  /** Catalog uploader resource for CSV-based catalog upload management */
  CatalogUploader = 'catalog/import',
  /** Catalog code mappings resource for managing size and color property mappings */
  CatalogCodeMappings = 'catalog/attributes',
  /** Storefront theme versions resource (view versions + trigger liquid theme deploys) */
  ThemeVersions = 'storefront/theme',
  /** Embedded Looker dashboards resource (partners with an active Looker service only) */
  LookerDashboards = 'analytics',
  /** SFTP-backed S3 file browser (Data Feeds > Files) */
  Files = 'files',
  /** Inventory items resource */
  InventoryItems = 'inventory',
  /** Shopify sync queue status resource (Storefront's Sync status tab) */
  ShopifySync = 'storefront/sync',
  /** Developer API documentation page (Developer section's API docs tab) */
  ApiDocs = 'dev/docs',
  /** Trade-in plugin installation guide (Developer section tab) */
  TradeinPluginGuide = 'dev/plugins/tradein',
  /** Resale plugin installation guide (Developer section tab) */
  ResalePluginGuide = 'dev/plugins/resale',
  /** Repairs plugin installation guide (Developer section tab) */
  RepairPluginGuide = 'dev/plugins/repair',
  /** Plugin analytics via Shopify + Google Tag Manager guide (Developer section tab) */
  GtmGuide = 'dev/gtm',
}

/**
 * Standard rejection reasons for repair requests and individual repair issues.
 * Used in both repair-level and issue-level rejection workflows.
 */
export const REJECTION_REASONS = [
  'Non-Repairable Damage Type',
  'Third-Party or Unauthorized Alterations',
  'Non-Authentic Product',
  'Item Outside Repair Program Scope',
  'Missing or Insufficient Information',
  'Unable to Determine Damage from Images',
] as const;

/**
 * Available catalog item availability options.
 * These values indicate where and how a catalog item can be traded in or purchased.
 */
export const CATALOG_AVAILABILITY_OPTIONS = [
  'Storefront',
  'Check-In',
  'Digital Trade-In',
  'In-Store Trade-In',
  'Repair',
] as const;

/**
 * Maps dashboard resources to their respective client plugin types.
 */
export const pluginResourceTypeMap: Record<PluginResource, PluginType> = {
  [DashboardResources.RecyclePlugins]: 'recycle',
  [DashboardResources.RepairPlugins]: 'repair',
  [DashboardResources.ResalePlugins]: 'resale',
  [DashboardResources.TradeinPlugins]: 'tradein',
};

export const pluginResourceProps: ResourceProps[] = [
  {
    name: DashboardResources.RecyclePlugins,
    options: {
      label: 'Recycle Plugin'
    },
  },
  {
    name: DashboardResources.RepairPlugins,
    options: {
      label: 'Repair Plugin'
    },
  },
  {
    name: DashboardResources.ResalePlugins,
    options: {
      label: 'Resale Plugin'
    },
  },
  {
    name: DashboardResources.TradeinPlugins,
    options: {
      label: 'Trade-in Plugin'
    },
  },
];
